import { omit } from 'lodash-es';
import { ctRefreshTokenRepository, CtTokenRepository } from 'common/api/models/ct-token.repository';
import { isCtTokenExpired } from 'utils/common';
import { doFetchExpiredToken, extractTokenPayload } from 'utils/token';
import { tokenExpiredErrorHandle } from 'utils/common';
import { getNewRelicAgent } from 'newRelicAgent';

export const BASE_URL = '/api/';

export const testPlayerPlayoutAvailable = (url) =>
  fetch(url).then((response) => {
    const { status, ok } = response;

    if (status >= 400 && !ok) {
      return Promise.reject(response);
    }
  });

/**
 * TODO - convert to async/await
 * @param {string} endpoint
 * @param {*} options - fetch() api options object
 * @param {string} base - base url
 * @return {Promise<T>}
 */
export const doFetch = (endpoint, options = {}, base = BASE_URL) => {
  if (base === BASE_URL) {
    options.headers = {
      ...options.headers,
      'ct-referrer': window.document.referrer,
    };
  }

  return (
    fetch(base + endpoint, options)
      // Await the promise of json(), and return both the raw response promise and the JSON promise
      .then((response) => {
        return response.text().then((text) => {
          try {
            return {
              json: JSON.parse(text ? text : '{}'),
              response,
            };
          } catch (e) {
            return Promise.reject(new SyntaxError('JSON parse error of: ' + text));
          }
        });
      })
      .then(({ json, response }) => {
        // if response with error update response object with response body and reject
        // .catch() will receive that object as error
        const { status, ok } = response;

        // send error to New Relic?
        const shouldBeReportedToNewRelic = status >= 400 && !ok && status !== 404 && status !== 403;

        if (shouldBeReportedToNewRelic) {
          // exclude Authorization for security reasons
          const requestOptions = options && {
            ...options,
            headers: omit(options.headers, ['Authorization']),
          };

          getNewRelicAgent().noticeError(
            new Error(`HTTP_${status}`),
            requestOptions && { requestOptions: JSON.stringify(requestOptions) }
          );
        }

        if (status >= 400 && !ok) {
          response['json'] = json;
          return Promise.reject(response);
        }

        if (status === 204) {
          return response;
        }

        // all good return json response
        return json;
      })
      .catch((error) => {
        tokenExpiredErrorHandle(error);
        if (error instanceof Error) {
          getNewRelicAgent().noticeError(error);
        }
        throw error;
      })
  );
};

/**
 * @param {string|null} token - CT JWT Token
 * @param {string} refreshToken
 * @param {string} endpoint
 * @param {*} options - fetch() api options object
 * @return {Promise<T>}
 */
export const preFetch = (token, refreshToken, endpoint, options = {}) => {
  if (token !== null) {
    const tokenPayload = extractTokenPayload(token);

    if (isCtTokenExpired(tokenPayload)) {
      return doFetchExpiredToken(endpoint, options, refreshToken, tokenPayload.user_id);
    }

    options.headers = {
      ...options.headers,
      ...{
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    };
  }

  return doFetch(endpoint, options);
};

export const callApiV2 = (endpoint, method = 'GET', data = null, json = true) => {
  return callApi(`v2/${endpoint}`, method, data, json);
};

/**
 * Helper function to perform API requests
 *
 * @param {String}      endpoint Endpoint URL (without / at the start) to request
 * @param {String}      method   HTTP method to make the request with (defaults to 'GET')
 * @param {Object|null} data     Optional POST/PUT data to append to the request body
 * @param {boolean}     json     request body format
 * @returns {Promise<T>}
 */
export const callApi = (endpoint, method = 'GET', data = null, json = true) => {
  const token = CtTokenRepository.fetch();
  const refreshToken = ctRefreshTokenRepository.fetch();

  const options = {
    method,
    headers: {},
    body: null,
  };

  if ((method === 'GET' || method === 'HEAD') && data !== null) {
    const error = new Error(`Method ${method} should not get body.`);
    const customAttributes = { endpoint, body: JSON.stringify(data) };
    getNewRelicAgent().noticeError(error, customAttributes);
  }

  if (data !== null) {
    if (!json) {
      const body = new FormData();
      Object.keys(data).forEach((key) => body.append(key, data[key]));
      options.body = body;
    } else {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(data);
    }
  }

  return preFetch(token, refreshToken, endpoint, options);
};
